/**
 * Catálogo de tipos de documento para captaciones (y flujos relacionados).
 *
 * Uso del desplegable (UX acordada):
 * 1) Lista principal: tipos en CODIGOS_FRECUENTES (~90% de uso) + una opción meta "Otros…".
 * 2) Al elegir "Otros…": se muestra el resto del catálogo agrupado + al final "Otro (no está en la lista)…"
 *    (codigo OTRO_NO_LISTADO) para texto libre / alta futura.
 *
 * Para cambiar qué sale arriba: edita solo CODIGOS_FRECUENTES (orden = orden en el desplegable).
 */

/** Valor de opción solo UI: abre el panel/lista secundaria con el resto del catálogo. No persistir en BD. */
export const META_ABRIR_OTROS_CATALOGO = "__meta_abrir_otros_catalogo__";

/**
 * Persistir en BD cuando el tipo no está en el catálogo; obligar a guardar descripción humana en paralelo
 * (p.ej. datos_extras.descripcion_tipo_documento o columna dedicada en captacion_documentos).
 */
export const OTRO_NO_LISTADO = "otro_no_listado";

/** Grupos para pintar la sublista "Otros" (acordeón / secciones). */
export const GRUPO = {
  CAPTACION: "captacion",
  IDENTIDAD: "identidad",
  REGISTRO: "registro",
  FISCAL: "fiscal",
  URBANISMO: "urbanismo",
  COMUNIDAD: "comunidad",
  VENTA: "venta",
  NOTARIA: "notaria",
  MARKETING: "marketing",
  GENERAL: "general",
};

/**
 * Tipos frecuentes (desplegable principal). Mover códigos entre aquí y el resto del catálogo según uso real.
 */
export const CODIGOS_FRECUENTES = [
  "nota_encargo",
  "nota_simple_informativa",
  "dni_nif_propietario",
  "ibi_recibo",
  "certificado_energetico",
  "cedula_habitabilidad",
  "escritura_compraventa",
  "plano_inmueble",
  "contrato_exclusiva",
  "reserva_arras",
  "dni_nif_comprador",
  "poder_notarial_venta",
  "minuta_notarial",
  "certificado_deuda_comunidad",
  "autorizacion_imagenes",
  "tasacion",
  "certificado_cargas",
  "hoja_firmas",
  "liquidacion_itp",
];

/** Catálogo completo: cada { codigo, etiqueta, grupo }. Códigos únicos en snake_case. */
export const DOCUMENTOS_TIPOS = [
  // —— Captación y encargo ——
  { codigo: "ficha_captacion", etiqueta: "Ficha de Captación", grupo: GRUPO.CAPTACION },
  { codigo: "nota_encargo", etiqueta: "Nota de encargo / encargo de venta", grupo: GRUPO.CAPTACION },
  { codigo: "inventario_muebles", etiqueta: "Inventario muebles", grupo: GRUPO.CAPTACION },
  { codigo: "contrato_exclusiva", etiqueta: "Contrato de exclusiva", grupo: GRUPO.CAPTACION },
  { codigo: "contrato_alquiler", etiqueta: "Contrato de alquiler", grupo: GRUPO.CAPTACION },
  { codigo: "anexo_exclusiva", etiqueta: "Anexo o prórroga de exclusiva", grupo: GRUPO.CAPTACION },
  { codigo: "condiciones_generales", etiqueta: "Condiciones generales / cláusulas tipo", grupo: GRUPO.CAPTACION },
  { codigo: "hoja_firmas", etiqueta: "Hoja de firmas / poderes simples del encargo", grupo: GRUPO.CAPTACION },
  { codigo: "identificacion_fiscal_propietario", etiqueta: "Identificación fiscal del propietario (modelo 033 u otro)", grupo: GRUPO.CAPTACION },
  { codigo: "certificado_energetico", etiqueta: "Certificado energético", grupo: GRUPO.CAPTACION },
  { codigo: "cedula_habitabilidad", etiqueta: "Cédula de habitabilidad / licencia primera ocupación", grupo: GRUPO.CAPTACION },
  { codigo: "libro_edificio", etiqueta: "Libro del edificio / ITE / IEE", grupo: GRUPO.CAPTACION },
  { codigo: "plano_inmueble", etiqueta: "Plano del inmueble (distribución)", grupo: GRUPO.CAPTACION },
  { codigo: "plano_situacion", etiqueta: "Plano de situación / emplazamiento", grupo: GRUPO.CAPTACION },
  { codigo: "memoria_calidades", etiqueta: "Memoria de calidades", grupo: GRUPO.CAPTACION },
  { codigo: "dossier_comercial", etiqueta: "Dossier comercial / ficha para compradores", grupo: GRUPO.CAPTACION },
  { codigo: "autorizacion_imagenes", etiqueta: "Autorización imágenes y datos (RGPD)", grupo: GRUPO.CAPTACION },
  { codigo: "comunicacion_comunidad", etiqueta: "Comunicación / autorización comunidad de propietarios", grupo: GRUPO.CAPTACION },

  // —— Identidad y titularidad ——
  { codigo: "dni_nif_propietario", etiqueta: "DNI / NIF del propietario", grupo: GRUPO.IDENTIDAD },
  { codigo: "dni_nif_propietario_reverso", etiqueta: "DNI / NIF del propietario (reverso)", grupo: GRUPO.IDENTIDAD },
  { codigo: "dni_nif_conyuge", etiqueta: "DNI / NIF del cónyuge", grupo: GRUPO.IDENTIDAD },
  { codigo: "libro_familia", etiqueta: "Libro de familia / certificado de matrimonio", grupo: GRUPO.IDENTIDAD },
  { codigo: "certificado_defuncion", etiqueta: "Certificado de defunción", grupo: GRUPO.IDENTIDAD },
  { codigo: "certificado_nacimiento", etiqueta: "Certificado literal de nacimiento", grupo: GRUPO.IDENTIDAD },
  { codigo: "poder_notarial_venta", etiqueta: "Poder notarial para venta", grupo: GRUPO.IDENTIDAD },
  { codigo: "poder_notarial_firma", etiqueta: "Poder notarial para firmar documentos", grupo: GRUPO.IDENTIDAD },
  { codigo: "certificado_penas", etiqueta: "Certificado de delitos de naturaleza sexual (comprador)", grupo: GRUPO.IDENTIDAD },

  // —— Registro y nota simple ——
  { codigo: "nota_simple_informativa", etiqueta: "Nota simple informativa", grupo: GRUPO.REGISTRO },
  { codigo: "nota_simple_negativa", etiqueta: "Nota simple negativa", grupo: GRUPO.REGISTRO },
  { codigo: "cert_grafica_catastral", etiqueta: "Ficha gráfica catastral", grupo: GRUPO.REGISTRO },
  { codigo: "certificado_cargas", etiqueta: "Certificado de cargas", grupo: GRUPO.REGISTRO },
  { codigo: "certificado_cargas_negativa", etiqueta: "Certificado de cargas negativo", grupo: GRUPO.REGISTRO },
  { codigo: "escritura_compraventa", etiqueta: "Escritura de compraventa (titularidad anterior)", grupo: GRUPO.REGISTRO },
  { codigo: "escritura_donacion", etiqueta: "Escritura de donación", grupo: GRUPO.REGISTRO },
  { codigo: "escritura_herencia", etiqueta: "Escritura de herencia / adjudicación", grupo: GRUPO.REGISTRO },
  { codigo: "escritura_divorcio", etiqueta: "Escritura de divorcio / liquidación gananciales", grupo: GRUPO.REGISTRO },
  { codigo: "adjudicacion_judicial", etiqueta: "Adjudicación judicial", grupo: GRUPO.REGISTRO },
  { codigo: "sentencia", etiqueta: "Sentencia judicial relevante", grupo: GRUPO.REGISTRO },
  { codigo: "inscripcion_registral", etiqueta: "Copia inscripción Registro de la Propiedad", grupo: GRUPO.REGISTRO },

  // —— Fiscal e impuestos ——
  { codigo: "ibi_recibo", etiqueta: "Recibo IBI", grupo: GRUPO.FISCAL },
  { codigo: "ibi_bonificacion", etiqueta: "Justificante bonificación / exención IBI", grupo: GRUPO.FISCAL },
  { codigo: "tasas_municipales", etiqueta: "Tasas municipales (basuras, vados, etc.)", grupo: GRUPO.FISCAL },
  { codigo: "plusvalia_municipal", etiqueta: "Liquidación / autoliquidación plusvalía municipal", grupo: GRUPO.FISCAL },
  { codigo: "modelo_347", etiqueta: "Modelo 347 u otro informativo", grupo: GRUPO.FISCAL },

  // —— Urbanismo ——
  { codigo: "licencia_obras", etiqueta: "Licencia de obras", grupo: GRUPO.URBANISMO },
  { codigo: "certificado_fin_obra", etiqueta: "Certificado final de obra / cédula", grupo: GRUPO.URBANISMO },
  { codigo: "declaracion_responsable", etiqueta: "Declaración responsable / comunicación", grupo: GRUPO.URBANISMO },
  { codigo: "informe_urbanistico", etiqueta: "Informe / certificado urbanístico", grupo: GRUPO.URBANISMO },
  { codigo: "certificado_no_afectacion", etiqueta: "Certificado no afectación planeamiento", grupo: GRUPO.URBANISMO },
  { codigo: "informe_geotecnico", etiqueta: "Informe geotécnico o estructural", grupo: GRUPO.URBANISMO },

  // —— Comunidad y suministros ——
  { codigo: "estatutos_comunidad", etiqueta: "Estatutos comunidad de propietarios", grupo: GRUPO.COMUNIDAD },
  { codigo: "actas_junta", etiqueta: "Actas de junta relevantes", grupo: GRUPO.COMUNIDAD },
  { codigo: "certificado_deuda_comunidad", etiqueta: "Certificado de deuda con la comunidad", grupo: GRUPO.COMUNIDAD },
  { codigo: "reglamento_zonas_comunes", etiqueta: "Reglamento interno (piscina, garaje, trastero)", grupo: GRUPO.COMUNIDAD },
  { codigo: "contrato_suministro_luz", etiqueta: "Contrato o última factura luz", grupo: GRUPO.COMUNIDAD },
  { codigo: "contrato_suministro_agua", etiqueta: "Contrato o última factura agua", grupo: GRUPO.COMUNIDAD },
  { codigo: "contrato_suministro_gas", etiqueta: "Contrato o última factura gas", grupo: GRUPO.COMUNIDAD },

  // —— Venta en curso ——
  { codigo: "reserva_arras", etiqueta: "Contrato de reserva / arras", grupo: GRUPO.VENTA },
  { codigo: "arras_justificante_pago", etiqueta: "Justificante pago arras", grupo: GRUPO.VENTA },
  { codigo: "ficha_cliente_comprador", etiqueta: "Ficha / KYC comprador", grupo: GRUPO.VENTA },
  { codigo: "dni_nif_comprador", etiqueta: "DNI / NIF comprador", grupo: GRUPO.VENTA },
  { codigo: "certificado_bancario", etiqueta: "Certificado bancario / IBAN liquidación", grupo: GRUPO.VENTA },
  { codigo: "oferta_vinculante", etiqueta: "Oferta vinculante / pre-aprobación hipoteca", grupo: GRUPO.VENTA },
  { codigo: "tasacion", etiqueta: "Tasación", grupo: GRUPO.VENTA },
  { codigo: "gastos_comprador_presupuesto", etiqueta: "Presupuesto gastos comprador", grupo: GRUPO.VENTA },
  { codigo: "gastos_vendedor_presupuesto", etiqueta: "Presupuesto gastos vendedor", grupo: GRUPO.VENTA },

  // —— Notaría ——
  { codigo: "minuta_notarial", etiqueta: "Minuta / borrador escritura notarial", grupo: GRUPO.NOTARIA },
  { codigo: "liquidacion_itp", etiqueta: "Liquidación ITP / autoliquidación", grupo: GRUPO.NOTARIA },
  { codigo: "liquidacion_plusvalia_venta", etiqueta: "Liquidación plusvalía (venta, vía notaría si aplica)", grupo: GRUPO.NOTARIA },
  { codigo: "justificante_pago_itp", etiqueta: "Justificante pago ITP", grupo: GRUPO.NOTARIA },
  { codigo: "certificado_hipoteca_cero", etiqueta: "Certificado cancelación hipoteca / deuda cero", grupo: GRUPO.NOTARIA },
  { codigo: "carta_pago_banco", etiqueta: "Carta de pago del banco (subrogación)", grupo: GRUPO.NOTARIA },
  { codigo: "documentacion_subrogacion", etiqueta: "Documentación subrogación hipotecaria", grupo: GRUPO.NOTARIA },
  { codigo: "comparecencia_previa_notaria", etiqueta: "Documentación comparecencia / previa notaría", grupo: GRUPO.NOTARIA },
  { codigo: "otros_notaria", etiqueta: "Otros documentos notaría (especificar en notas)", grupo: GRUPO.NOTARIA },

  // —— Marketing ——
  { codigo: "brief_marketing", etiqueta: "Brief comercial / argumentario", grupo: GRUPO.MARKETING },
  { codigo: "plan_fotos_video", etiqueta: "Plan sesión fotos / vídeo", grupo: GRUPO.MARKETING },
  { codigo: "autorizacion_redes", etiqueta: "Autorización publicación redes", grupo: GRUPO.MARKETING },
  { codigo: "creatividad_anuncio", etiqueta: "Creatividades (banners, stories)", grupo: GRUPO.MARKETING },
  { codigo: "informe_competencia", etiqueta: "Informe competencia / precios zona", grupo: GRUPO.MARKETING },
  { codigo: "listado_portales", etiqueta: "Listado / capturas alta en portales", grupo: GRUPO.MARKETING },
  { codigo: "informe_seo", etiqueta: "Informe SEO / keywords", grupo: GRUPO.MARKETING },
  { codigo: "material_open_house", etiqueta: "Material open house / visita", grupo: GRUPO.MARKETING },

  // —— General ——
  { codigo: "correo_exportado", etiqueta: "Email / hilo relevante exportado", grupo: GRUPO.GENERAL },
  { codigo: "foto_documento", etiqueta: "Foto de documento (no PDF)", grupo: GRUPO.GENERAL },
  { codigo: "documento_generico", etiqueta: "Documento genérico (especificar en notas)", grupo: GRUPO.GENERAL },
  { codigo: "informe_pericial", etiqueta: "Informe pericial", grupo: GRUPO.GENERAL },
  { codigo: "seguro_hogar", etiqueta: "Póliza seguro hogar", grupo: GRUPO.GENERAL },
  { codigo: "documentacion_banco_generica", etiqueta: "Documentación banco (genérico)", grupo: GRUPO.GENERAL },
];

const _map = new Map(DOCUMENTOS_TIPOS.map((t) => [t.codigo, t]));

/** Etiqueta legible por código (o cadena vacía si no existe). */
export function etiquetaTipoDocumento(codigo) {
  if (codigo === OTRO_NO_LISTADO) return "Otro (no está en la lista)";
  const t = _map.get(codigo);
  return t ? t.etiqueta : "";
}

/** Opciones del desplegable principal: frecuentes + meta "Otros…". */
export function getOpcionesDesplegablePrincipal() {
  const out = [];
  for (const codigo of CODIGOS_FRECUENTES) {
    const t = _map.get(codigo);
    if (t) out.push({ codigo: t.codigo, etiqueta: t.etiqueta, esMeta: false });
  }
  out.push({
    codigo: META_ABRIR_OTROS_CATALOGO,
    etiqueta: "Otros…",
    esMeta: true,
  });
  return out;
}

/** Resto del catálogo (excluye frecuentes), agrupado, + fila final "Otro no listado". */
export function getTiposAgrupadosParaPanelOtros() {
  const freq = new Set(CODIGOS_FRECUENTES);
  const resto = DOCUMENTOS_TIPOS.filter((t) => !freq.has(t.codigo));
  const porGrupo = {};
  for (const g of Object.values(GRUPO)) {
    porGrupo[g] = [];
  }
  for (const t of resto) {
    const g = porGrupo[t.grupo] ? t.grupo : GRUPO.GENERAL;
    porGrupo[g].push(t);
  }
  const ordenGrupos = [
    GRUPO.CAPTACION,
    GRUPO.IDENTIDAD,
    GRUPO.REGISTRO,
    GRUPO.FISCAL,
    GRUPO.URBANISMO,
    GRUPO.COMUNIDAD,
    GRUPO.VENTA,
    GRUPO.NOTARIA,
    GRUPO.MARKETING,
    GRUPO.GENERAL,
  ];
  const secciones = ordenGrupos
    .map((grupo) => ({
      grupo,
      etiquetaGrupo: etiquetaGrupoDocumentos(grupo),
      tipos: porGrupo[grupo] || [],
    }))
    .filter((s) => s.tipos.length > 0);

  return {
    secciones,
    opcionOtrosNoListado: {
      codigo: OTRO_NO_LISTADO,
      etiqueta: "Otro (no está en la lista)…",
      esMeta: false,
    },
  };
}

export function etiquetaGrupoDocumentos(grupo) {
  const m = {
    [GRUPO.CAPTACION]: "Captación y encargo",
    [GRUPO.IDENTIDAD]: "Identidad y titularidad",
    [GRUPO.REGISTRO]: "Registro y nota simple",
    [GRUPO.FISCAL]: "Fiscal e impuestos",
    [GRUPO.URBANISMO]: "Urbanismo",
    [GRUPO.COMUNIDAD]: "Comunidad y suministros",
    [GRUPO.VENTA]: "Venta en curso",
    [GRUPO.NOTARIA]: "Notaría",
    [GRUPO.MARKETING]: "Marketing",
    [GRUPO.GENERAL]: "General",
  };
  return m[grupo] || grupo;
}

/** Todos los códigos válidos del catálogo (sin meta ni otro_no_listado). */
export function getTodosCodigosCatalogo() {
  return DOCUMENTOS_TIPOS.map((t) => t.codigo);
}

/** ¿El código pertenece al catálogo fijo (excl. otro_no_listado)? */
export function esCodigoCatalogoValido(codigo) {
  return codigo != null && codigo !== "" && _map.has(codigo);
}
